import { useState } from "react";
import { useHttp } from "../hooks/useHttp";

interface SupportFormData {
  issueType: string;
  subject: string;
  email: string;
  description: string;
  media: File | null;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes

export function Support() {
  const { sendRequest, isLoading, error } = useHttp();
  const [formData, setFormData] = useState<SupportFormData>({
    issueType: "",
    subject: "",
    email: "",
    description: "",
    media: null,
  });
  const [fileError, setFileError] = useState<string>("");
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;

    if (file && file.size > MAX_FILE_SIZE) {
      setFileError("File size must be less than 5MB");
      setFormData((prev) => ({ ...prev, media: null }));
      return;
    }

    setFileError("");
    setFormData((prev) => ({ ...prev, media: file }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (fileError) {
      return;
    }

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("issueType", formData.issueType);
      formDataToSend.append("subject", formData.subject);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("description", formData.description);

      if (formData.media) {
        formDataToSend.append("media", formData.media);
      }

      const response = await sendRequest({
        endpoint: "support/submit",
        method: "POST",
        payload: formDataToSend,
      });

      console.log("Support ticket submitted successfully:", response.data);

      setIsSubmitted(true);

      setFormData({
        issueType: "",
        subject: "",
        email: "",
        description: "",
        media: null,
      });
      setFileError("");

      setTimeout(() => {
        setIsSubmitted(false);
      }, 2000);
    } catch (err) {
      console.error("Failed to submit support ticket:", err);
    }
  };

  return (
    <div className="px-4 py-8">
      <div className="bg-[#313131] py-8 px-6 w-full max-w-md">
        <h2 className="mb-2 text-2xl font-bold text-center text-[#FCFCFA]">
          Facing an Issue?
        </h2>

        <p className="mb-6 text-sm text-center text-[#FCFCFA]">
          Submit a support ticket, or view your previous tickets below.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="px-4 py-3 text-sm text-red-400 rounded border bg-red-500/20 border-red-500/50">
              {error}
            </div>
          )}

          <div>
            <select
              name="issueType"
              value={formData.issueType}
              onChange={handleInputChange}
              className="p-3 w-full text-[#FCFCFA] invalid:text-gray-400 [&>option]:text-black  bg-transparent border border-[#FCFCFA66] appearance-none cursor-pointer focus:outline-none focus:border-[#FCFCFACC] disabled:opacity-50 disabled:cursor-not-allowed"
              required
              disabled={isLoading}
            >
              <option value="" disabled>
                Select Issue Type
              </option>
              <option value="technical">Technical Issue</option>
              <option value="account">Account Issue</option>
              <option value="payment">Payment Issue</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <input
              type="text"
              name="subject"
              value={formData.subject}
              onChange={handleInputChange}
              placeholder="Subject"
              className="p-3 w-full placeholder-[#FCFCFACC] text-[#FCFCFA] bg-transparent border border-[#FCFCFA66] focus:outline-none focus:border-[#FCFCFACC] disabled:opacity-50 disabled:cursor-not-allowed"
              required
              disabled={isLoading}
            />
          </div>

          <div>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Email Address"
              className="p-3 w-full placeholder-[#FCFCFACC] text-[#FCFCFA] bg-transparent border border-[#FCFCFA66] focus:outline-none focus:border-[#FCFCFACC] disabled:opacity-50 disabled:cursor-not-allowed"
              required
              disabled={isLoading}
            />
          </div>

          <div>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Issue Description"
              rows={4}
              className="p-3 w-full placeholder-[#FCFCFACC] text-[#FCFCFA] bg-transparent border border-[#FCFCFA66] resize-none focus:outline-none focus:border-[#FCFCFACC] disabled:opacity-50 disabled:cursor-not-allowed"
              required
              disabled={isLoading}
            />
          </div>

          <div>
            <label className="flex items-center text-sm text-[#FCFCFA] cursor-pointer gap-2 pl-3">
              <input
                type="file"
                onChange={handleFileChange}
                className="hidden"
                accept="image/*,video/*"
                disabled={isLoading}
              />

              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g opacity="0.8">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M12.4734 11.7102C13.0046 11.1789 13.3031 10.4583 13.3031 9.70688C13.3031 8.9555 13.0046 8.23489 12.4734 7.70355L8.11338 3.34355C7.98828 3.21854 7.91797 3.04896 7.91791 2.87212C7.91785 2.69527 7.98804 2.52564 8.11304 2.40055C8.23805 2.27545 8.40763 2.20514 8.58447 2.20508C8.76132 2.20502 8.93095 2.27521 9.05604 2.40021L13.416 6.76021C14.1975 7.54154 14.6367 8.60132 14.6368 9.70641C14.6369 10.8115 14.198 11.8714 13.4167 12.6529C12.6354 13.4344 11.5756 13.8735 10.4705 13.8736C9.36543 13.8738 8.30555 13.4349 7.52404 12.6535L2.22138 7.35021C1.65868 6.78742 1.34259 6.02415 1.34265 5.22831C1.34271 4.43247 1.65892 3.66925 2.22171 3.10655C2.7845 2.54385 3.54777 2.22776 4.34361 2.22782C5.13945 2.22788 5.90268 2.54409 6.46538 3.10688L11.7674 8.40888C12.1031 8.75465 12.2893 9.21863 12.2857 9.70056C12.2821 10.1825 12.0891 10.6437 11.7482 10.9844C11.4074 11.3251 10.9462 11.5181 10.4643 11.5215C9.98233 11.525 9.51839 11.3387 9.17271 11.0029L4.34204 6.17155C4.21695 6.04654 4.14664 5.87696 4.14658 5.70012C4.14651 5.52327 4.2167 5.35364 4.34171 5.22855C4.46672 5.10345 4.63629 5.03314 4.81314 5.03308C4.98999 5.03302 5.15962 5.10321 5.28471 5.22821L10.116 10.0595C10.2099 10.1535 10.3373 10.2064 10.4701 10.2064C10.603 10.2065 10.7304 10.1538 10.8244 10.0599C10.9184 9.96599 10.9712 9.83862 10.9712 9.70578C10.9713 9.57294 10.9186 9.44552 10.8247 9.35155L5.52271 4.04955C5.36793 3.89467 5.18415 3.77181 4.98188 3.68796C4.77961 3.60411 4.56281 3.56092 4.34385 3.56086C4.12489 3.56079 3.90806 3.60386 3.70574 3.68759C3.50343 3.77133 3.31958 3.8941 3.16471 4.04888C2.85193 4.36148 2.67614 4.78553 2.67602 5.22774C2.67589 5.66995 2.85144 6.0941 3.16404 6.40688L8.46671 11.7095C8.99805 12.2408 9.71866 12.5393 10.47 12.5393C11.2214 12.5393 11.942 12.2408 12.4734 11.7095V11.7102Z"
                    fill="#FCFCFA"
                  />
                </g>
              </svg>

              <span>Attach Media</span>

              {formData.media && (
                <span className="ml-2 text-white max-w-[200px] truncate">
                  ({formData.media.name})
                </span>
              )}
            </label>
            {fileError && (
              <p className="pl-3 mt-1 text-xs text-red-400">{fileError}</p>
            )}
          </div>

          <div className="pt-2">
            <button
              type="submit"
              className="py-2 w-full font-bold text-lg text-[#313131] bg-white transition-colors duration-200 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              disabled={isLoading || isSubmitted}
            >
              {isLoading ? (
                <>
                  <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Submitting...
                </>
              ) : isSubmitted ? (
                <>
                  <svg
                    className="w-4 h-4"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Ticket Submitted!
                </>
              ) : (
                "Submit Ticket"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
